import { Colors as ColorCollections, Themes } from '../constants/themeConstants';

import { QUERY_KEYS } from '@/lib';
import { queryClient } from '@/queries';
import { Colors, Header, ImageFrames, PageLocation, Section, StoreTheme } from '@/types/store';
import { Page } from '@/types/pageSchema';
import { User } from '@/types/user';
import { cloneDeep } from 'lodash';
import { create } from 'zustand';
import { updatePage, updatePagesOrder } from '../api/pages.api';
import { createSection, deleteSection, updateSection, updateSectionsOrder } from '../api/sections.api';
import { updateStoreData } from '../api/store.api';
import { generateNewSectionId, toStoreItems } from '../logic/customizationStore.logic';
import {
    buildStoreData,
    getPageButton,
    getPageOrderItems,
    getSection,
    getSectionOrderItems,
    hasStoreDataChanges,
    identifyStoreChanges,
    isSection,
} from '../logic/diffState.logic';
import { createCachedStateSnapshot, setupIsDirtySubscription } from '../logic/dirtyState.logic';
import { renumberAndSortItems } from '../logic/sort.logic';
import { validateHeader, validateStoreItems } from '../logic/validate.logic';
import { CustomizationsConfiguration, Fonts, ItemType, SecondaryFonts, StoreData, StoreItem } from '../types/store';
export interface CustomizationStoreState {
    isLoading: boolean;
    storeId: number | null;
    theme: StoreTheme | null;
    primaryFont: Fonts | null;
    secondaryFont: SecondaryFonts | null;
    colors: Colors | null;
    imageFrames: ImageFrames | null;
    header: Header | null;

    // Store content
    sections: Section[];
    pages: Page[]; // Non-landing pages
    storeItems: StoreItem[];

    // Handling local state
    cachedState: Partial<CustomizationStoreState> | null;
    isDirty: boolean;

    // Actions
    setStore: ({
        storeId,
        pages,
        sections,
        data,
        user,
    }: {
        storeId: number;
        pages: Page[];
        sections: Section[];
        data: StoreData;
        user: User;
    }) => void;
    clearDefaultStore: () => void;
    updateStoreCustomizations: (customizations: Partial<CustomizationsConfiguration>) => void;
    updateStoreHeader: (header: Partial<Header>) => void;
    updateItem: (item: StoreItem) => void;
    addItem: (item: Omit<StoreItem, 'id'>, atOrder?: number) => number; // Returns the new item ID
    reorder: (id: number, newOrder: number) => void;
    deleteItem: (id: number) => void;
    clearPendingItems: () => void;
    revert: () => void;
    publish: () => Promise<{
        success: boolean;
        validation?: boolean;
        errors?: { id: number | string; errors: string[] }[];
    }>;
    setIsLoading: (isLoading: boolean) => void;
}
export const useCustomizationStore = create<CustomizationStoreState>()(set => ({
    // Initial state
    storeId: null,
    isLoading: false,
    theme: null,
    primaryFont: null,
    secondaryFont: null,
    colors: null,
    imageFrames: null,
    header: null,
    sections: [],
    pages: [],
    storeItems: [], // Unified store items (pages and sections)
    cachedState: null,
    isDirty: false,

    // Actions
    setStore: ({ storeId, pages, sections, data, user }) => {
        const currentState = useCustomizationStore.getState();

        // Since react-query fetched data in background, we don't want to overwrite the current state if it's dirty.
        // This is to prevent the user from losing their changes.
        if (currentState.isDirty) return;

        const { theme, primary_font, secondary_font, colors, image_frame, header } = data;
        const nonLandingPages = pages.filter(p => p.data.location !== PageLocation.LANDING);
        const storeItems = toStoreItems(pages, sections, user);

        const storeData = {
            storeId,
            theme: theme || StoreTheme.BRIM,
            primaryFont: primary_font || null,
            secondaryFont: secondary_font || null,
            colors: colors?.default ?? null,
            imageFrames: image_frame || null,
            storeItems,
            isDirty: false,
            sections,
            pages: nonLandingPages,
            header: header ?? {
                title: user.full_name,
                bio: user.bio ?? '',
                image: user.profile_image,
            },
        };

        // Create initial cachedState snapshot
        const cachedState = createCachedStateSnapshot(storeData as Partial<CustomizationStoreState>);

        set({
            ...(storeData as Partial<CustomizationStoreState>),
            cachedState,
            isDirty: false,
        });
    },

    clearDefaultStore: () => {
        set({
            theme: StoreTheme.DEFAULT,
            primaryFont: null,
            secondaryFont: null,
            colors: null,
            imageFrames: null,
            header: null,
            sections: [],
            pages: [],
            storeItems: [],
            isDirty: false,
        });
    },

    updateStoreCustomizations: (customizations: Partial<CustomizationsConfiguration>) => {
        set(_state => {
            // Extract fonts from customizations, fallback to current state
            const { fonts, imageFrames, colors, ...rest } = customizations;

            // Find theme if specified
            const selectedTheme = customizations.theme ? Themes.find(t => t.id === customizations.theme) : undefined;
            if (selectedTheme) {
                const themeColorCollection = ColorCollections.find(c => c.id === selectedTheme.colors);
                const themeColors = themeColorCollection?.colors;

                return {
                    theme: selectedTheme.id,
                    primaryFont: fonts?.primary ?? selectedTheme.fonts?.primary ?? _state.primaryFont,
                    secondaryFont: fonts?.secondary ?? selectedTheme.fonts?.secondary ?? _state.secondaryFont,
                    colors: colors ?? themeColors ?? _state.colors,
                    imageFrames: imageFrames ?? selectedTheme.imageFrames ?? _state.imageFrames,
                };
            }

            return {
                ...rest,
                primaryFont: fonts?.primary ?? _state.primaryFont,
                secondaryFont: fonts?.secondary ?? _state.secondaryFont,
                imageFrames: imageFrames ?? _state.imageFrames,
                colors: colors ?? _state.colors,
            };
        });
    },
    updateStoreHeader: (header: Partial<Header>) => {
        set(_state => ({
            header: { ..._state.header, ...header } as Header,
        }));
    },
    updateItem: (item: StoreItem) => {
        set(_state => {
            const updated = _state.storeItems.map(block => (block.id === item.id ? { ...item } : block));
            const storeItems = renumberAndSortItems(updated);
            return { ..._state, storeItems };
        });
    },
    addItem: (item: Omit<StoreItem, 'id'>, atOrder?: number) => {
        const { storeItems } = useCustomizationStore.getState();
        const id = generateNewSectionId(storeItems);
        set(_state => {
            const next = _state.storeItems.slice();
            const insertIndex = Math.max(0, Math.min(atOrder ?? next.length, next.length));
            // Cast is needed because Omit<StoreItem, 'id'> loses the discriminant
            next.splice(insertIndex, 0, { ...item, id } as StoreItem);
            const storeItems = renumberAndSortItems(next);
            return { ..._state, storeItems };
        });
        return id;
    },
    reorder: (id: number, newOrder: number) => {
        set(_state => {
            const currentIndex = _state.storeItems.findIndex(b => b.id === id);

            if (currentIndex === -1) return _state;
            const remaining = _state.storeItems.slice();
            const [moving] = remaining.splice(currentIndex, 1);
            if (!moving) return _state;
            const insertIndex = Math.max(0, Math.min(newOrder, remaining.length));
            remaining.splice(insertIndex, 0, moving);

            const storeItems = remaining.map((b, idx) => ({ ...b, order: idx }));

            return { ..._state, storeItems };
        });
    },
    deleteItem: (id: number) => {
        set(_state => {
            const remaining = _state.storeItems.filter(item => item.id !== id);
            const storeItems = renumberAndSortItems(remaining);
            return { ..._state, storeItems };
        });
    },
    clearPendingItems: () => {
        set(_state => {
            const remaining = _state.storeItems.filter(item => item.type !== ItemType.PENDING);
            const storeItems = renumberAndSortItems(remaining);
            return { ..._state, storeItems };
        });
    },
    revert: () => {
        set(_state => {
            if (!_state.cachedState) return _state;

            const restored = cloneDeep(_state.cachedState);
            const storeItems = restored.storeItems ? renumberAndSortItems(restored.storeItems) : _state.storeItems;

            return {
                ..._state,
                ...restored,
                storeItems,
                isDirty: false,
            } as CustomizationStoreState;
        });
    },
    publish: async () => {
        let state = useCustomizationStore.getState();

        // Validate all blocks before publishing
        const validationErrors = validateStoreItems(state.storeItems);
        if (validationErrors) return { success: false, validation: true, errors: validationErrors };

        // Validate header before publishing
        if (state.header) {
            const headerValidationErrors = validateHeader(state.header);
            if (headerValidationErrors) return { success: false, validation: true, errors: headerValidationErrors };
        }

        const storeId = state.storeId;
        if (!storeId) {
            return { success: false, validation: false, errors: [{ id: 0, errors: ['Store ID not found'] }] };
        }

        // Identify what has changed since the last publish
        const changes = identifyStoreChanges(state, state.cachedState);

        try {
            set({ isLoading: true });
            // Sync section changes to backend
            const { sections: sectionChanges, pages: pageChanges } = changes;

            // 0. Create new sections (added sections)
            state = useCustomizationStore.getState();
            for (const sectionId of sectionChanges.added) {
                const section = getSection(state, sectionId);
                if (!section) {
                    console.warn(`[ThemeEditor:Publish] Section data not found for section ${sectionId}`);
                    continue;
                }
                const { data, type } = section;
                const response = await createSection(storeId, { ...data, type });
                const newSectionId = response.data.section.section_id;

                // If the section ID from the backend is different from the local section ID, update the local section ID
                if (section.id !== newSectionId) {
                    set(_state => {
                        // Update storeItems array
                        const updatedStoreItems = _state.storeItems.map(item => {
                            if (item.id === sectionId && isSection(item)) return { ...item, id: newSectionId };
                            return item;
                        });

                        // Update sections array
                        const updatedSections = _state.sections.map(section => {
                            if (section.section_id === sectionId) return { ...section, section_id: newSectionId };
                            return section;
                        });

                        return {
                            ..._state,
                            storeItems: updatedStoreItems,
                            sections: updatedSections,
                        };
                    });

                    // Get fresh state after ID updates to ensure subsequent operations use correct IDs
                    state = useCustomizationStore.getState();
                }
            }

            // 1. Delete removed sections
            for (const sectionId of sectionChanges.removed) {
                await deleteSection(storeId, sectionId);
            }

            // 2. Update sections with changed data
            state = useCustomizationStore.getState();
            for (const sectionId of sectionChanges.updated) {
                const section = getSection(state, sectionId);
                if (!section) {
                    console.warn(`[ThemeEditor:Publish] Section data not found for section ${sectionId}`);
                    continue;
                }
                const { data, type } = section;
                await updateSection(storeId, sectionId, { ...data, type });
            }

            // 3. Update section order (if any sections were reordered, send all sections with their new order)
            if (sectionChanges.reordered.length > 0) {
                state = useCustomizationStore.getState();
                const sectionOrderItems = getSectionOrderItems(state);
                await updateSectionsOrder(storeId, sectionOrderItems);
            }

            // Sync page changes to backend
            // 1. Update page order (if any pages were reordered, send all pages with their new order)
            if (pageChanges.reordered.length > 0) {
                state = useCustomizationStore.getState();
                const pageOrderItems = getPageOrderItems(state);
                await updatePagesOrder(storeId, pageOrderItems);
            }

            // 2. Update pages that were updated (specifically button changes)
            state = useCustomizationStore.getState();
            for (const pageId of pageChanges.updated) {
                const button = getPageButton(state, pageId);

                const page = state.pages.find(p => p.page_id === pageId) ?? null;
                if (!button || !page) {
                    console.warn(`[ThemeEditor:Publish] Button data not found for page ${pageId}`);
                    continue;
                }
                await updatePage(storeId, {
                    page_id: pageId,
                    ...page.data,
                    button: {
                        ...button,
                    },
                });
            }

            // Only update if storeData has changed compared to cachedState
            state = useCustomizationStore.getState();
            if (hasStoreDataChanges(state, state.cachedState)) {
                const storeData = buildStoreData(state);
                await updateStoreData(storeId, storeData);
            }

            // All blocks are valid and synced, proceed with publishing. Create a new cached state snapshot.
            set(_state => {
                return {
                    ..._state,
                    isDirty: false,
                    cachedState: createCachedStateSnapshot(_state),
                };
            });

            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
            return { success: true };
        } catch (error) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
            const errorMessage = error instanceof Error ? error.message : 'Unknown API error';
            console.error(`[ThemeEditor:Publish] Failed to sync changes to backend (storeId: ${storeId}):`, error);

            return {
                success: false,
                validation: false,
                errors: [{ id: 0, errors: [errorMessage] }],
            };
        } finally {
            set({ isLoading: false });
        }
    },

    setIsLoading: (isLoading: boolean) => {
        set({ isLoading });
    },
}));

// Setup automatic isDirty calculation with debouncing
setupIsDirtySubscription(useCustomizationStore);
